/**
 * Cache Helper Utility
 *
 * Provides in-memory caching with TTL (time-to-live) support for service layer data.
 * Reduces unnecessary database/API calls and improves application performance.
 *
 * @example
 * const cache = new CacheHelper<Story[]>(5 * 60 * 1000); // 5 minute TTL
 *
 * // Set cache
 * cache.set('stories', storiesData);
 *
 * // Get cache (returns null if expired or not found)
 * const cached = cache.get('stories');
 *
 * // Clear specific cache
 * cache.clear('stories');
 *
 * // Clear all cache
 * cache.clearAll();
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

export class CacheHelper<T> {
  private cache: Map<string, CacheEntry<T>> = new Map();
  private ttl: number;

  /**
   * Create a new cache helper
   * @param ttl Time-to-live in milliseconds (default: 5 minutes)
   */
  constructor(ttl: number = 5 * 60 * 1000) {
    this.ttl = ttl;
  }

  /**
   * Get cached data if valid, otherwise return null
   * @param key Cache key
   * @returns Cached data or null if expired/not found
   */
  get(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    const now = Date.now();
    const isExpired = now - entry.timestamp > this.ttl;

    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set cache data
   * @param key Cache key
   * @param data Data to cache
   */
  set(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Check if cache exists and is valid
   * @param key Cache key
   * @returns True if valid cache exists
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Clear specific cache entry
   * @param key Cache key
   */
  clear(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clearAll(): void {
    this.cache.clear();
  }

  /**
   * Get cache size
   * @returns Number of cache entries
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Update TTL for future cache entries
   * @param newTtl New TTL in milliseconds
   */
  setTTL(newTtl: number): void {
    this.ttl = newTtl;
  }
}

/**
 * Global cache instances for different data types
 * These can be imported and used across services
 */

// Story cache - 5 minutes
export const storyCache = new CacheHelper<any>(5 * 60 * 1000);

// User profile cache - 3 minutes (profiles change less frequently)
export const userCache = new CacheHelper<any>(3 * 60 * 1000);

// Auth cache - 1 minute (auth state should be fresh)
export const authCache = new CacheHelper<any>(1 * 60 * 1000);

// Badge cache - 10 minutes (badges don't change often)
export const badgeCache = new CacheHelper<any>(10 * 60 * 1000);

// Quiz cache - 10 minutes (quizzes are relatively static)
export const quizCache = new CacheHelper<any>(10 * 60 * 1000);

// Quiz attempt cache - 2 minutes (attempts should be fresh during active quiz taking)
export const quizAttemptCache = new CacheHelper<any>(2 * 60 * 1000);

// KPI data cache - 3 minutes (KPI data changes only when students complete activities)
export const kpiCache = new CacheHelper<any>(3 * 60 * 1000);

/**
 * Clear all caches
 * Useful for logout or when data needs to be refreshed
 */
export function clearAllCaches(): void {
  storyCache.clearAll();
  userCache.clearAll();
  authCache.clearAll();
  badgeCache.clearAll();
  quizCache.clearAll();
  quizAttemptCache.clearAll();
  kpiCache.clearAll();
}

/**
 * Generate cache key from multiple parameters
 * @param parts Key parts to combine
 * @returns Combined cache key
 *
 * @example
 * const key = generateCacheKey('user', userId, 'stories');
 * // Returns: 'user:123:stories'
 */
export function generateCacheKey(...parts: (string | number | undefined)[]): string {
  return parts.filter(p => p !== undefined).join(':');
}
